import {
  coerceColor,
  deserializeColor,
  drawingEntityBaseSchema,
  serializeColor,
} from "@dxf-ai-editor/drawing-model";
import type { DrawingEntity, DrawingEntityBase } from "@dxf-ai-editor/drawing-model";
import { internString, lookupString, type SharedStringTable } from "./sharedStringTable";

/* ── The common-metadata remainder ────────────────────────────────────── */

/**
 * The `DrawingEntityBase` keys this module gives a DEDICATED COLUMN to.
 *
 * `satisfies` — not a bare `as const` — so a typo or a key that stops
 * existing on the model is a compile error rather than a silently dead
 * entry that would push a real field into the remainder twice.
 */
const BASE_COLUMN_KEYS = [
  "id",
  "spaceId",
  "layer",
  "color",
  "linetype",
  "lineweight",
  "transparency",
  "arrayMode",
  "arrayGroupId",
  "arraySourceOfGroup",
] as const satisfies readonly (keyof DrawingEntityBase)[];

type BaseColumnKey = (typeof BASE_COLUMN_KEYS)[number];

/**
 * Everything on `DrawingEntityBase` that the columns above do NOT carry —
 * today `linetypeScale` (48), `ownerHandle` (330), `materialHandle` (347),
 * `plotStyleHandle` (390), `visibility` (60), `shadowMode` (284),
 * `extensionDictionaryHandle` (the 360 inside `102 {ACAD_XDICTIONARY`) and
 * the `extraGroups` vendor/unknown-group passthrough.
 *
 * # Why this is a remainder and not eight more columns
 *
 * Every store in this directory rebuilds its entities out of these columns on
 * the STREAMED import path (`≥ STREAMING_IMPORT_FILE_SIZE_THRESHOLD`, where
 * `discardEntitiesAfterStreaming` empties `space.entities[]`), so a base field
 * with no carry here is a field DELETED from the user's file on open-and-save
 * — silently, and only for the big drawings these stores exist to serve. That
 * had already happened three times in a row, one hand-added column at a time
 * (per-VERTEX metadata, then SEQEND metadata, then the `ACAD_TABLE` extension
 * dictionary @codex PR #789 round 3 P1), with `ownerHandle` still outstanding
 * and written into `streamingSeqendMetadata.polyline.test.ts` as a named
 * byte-equality exception. Enumerating the fields we happen to know about is
 * the defect; the fourth column would only have moved the next loss one field
 * further along.
 *
 * So the key set is DERIVED, at runtime, from `drawingEntityBaseSchema` minus
 * the columns — a field added to the base schema tomorrow rides along here
 * with no change to this file — and the type is the matching `Omit<…>` of the
 * same key set, so the two cannot drift without a compile error. Same shape,
 * and for the same reason, as `compactHeavyPolylineStore.ts`'s
 * `vertexMetadata` / `seqendMetadata`.
 */
export type BaseMetadata = Omit<DrawingEntityBase, BaseColumnKey>;

const BASE_COLUMN_KEY_SET: ReadonlySet<string> = new Set<string>(
  BASE_COLUMN_KEYS,
);

/**
 * Base-schema keys with no column, in schema order. Exported so the census
 * test can assert the derivation really is open-ended rather than a second
 * hand-written list.
 */
export const BASE_METADATA_KEYS: readonly (keyof BaseMetadata)[] =
  Object.keys(drawingEntityBaseSchema.shape).filter(
    (key) => !BASE_COLUMN_KEY_SET.has(key),
  ) as (keyof BaseMetadata)[];

/**
 * The remainder of `entity`'s BASE fields, or `undefined` when it has none.
 *
 * PRESENCE, NOT SENTINELS: a key whose value is `undefined` is not copied, so
 * an entity whose file carried no `330` comes back with `ownerHandle` ABSENT
 * rather than present-and-empty. DXF treats "group absent" and "group written
 * empty" as different data and so does the writer, which emits `330` only when
 * the field exists.
 *
 * Iterates `BASE_METADATA_KEYS` rather than rest-destructuring the argument
 * (the shape `compactHeavyPolylineStore` uses for one exact record type):
 * every caller passes a FULL `DrawingEntity`, so a rest spread here would
 * sweep the entire per-type geometry payload into the remainder. Reading the
 * base schema's own key list keeps the remainder to base fields while staying
 * just as open-ended.
 */
export function captureBaseMetadata(
  entity: DrawingEntityBase,
): BaseMetadata | undefined {
  let metadata: Record<string, unknown> | undefined;
  const source = entity as unknown as Record<string, unknown>;
  for (let i = 0; i < BASE_METADATA_KEYS.length; i++) {
    const key = BASE_METADATA_KEYS[i]!;
    const value = source[key];
    if (value === undefined) continue;
    (metadata ??= {})[key] = value;
  }
  return metadata as BaseMetadata | undefined;
}

/** Re-attach a captured remainder to a rebuilt entity. No-op when absent. */
export function applyBaseMetadata(
  target: DrawingEntityBase,
  metadata: BaseMetadata | undefined,
): void {
  if (metadata !== undefined) Object.assign(target, metadata);
}

/** `set` when the entity has a remainder, `delete` when it does not. */
export function storeBaseMetadata(
  metadataByIndex: Map<number, BaseMetadata>,
  index: number,
  entity: DrawingEntityBase,
): void {
  const metadata = captureBaseMetadata(entity);
  if (metadata !== undefined) metadataByIndex.set(index, metadata);
  else metadataByIndex.delete(index);
}

/**
 * Heap cost of ONE populated remainder entry: a V8 `OrderedHashMap` slot (key +
 * value + chain, at a ~2× load factor) plus the `JSObject` header of the small
 * carried object, plus 8 bytes per key it holds.
 *
 * Calibrated, not guessed: 200 000 LINEs built through `appendEntity` with
 * `--expose-gc` measured 46.3 B/entity of retained heap with NO remainder and
 * 138.7 B/entity with `ownerHandle` on every entity — a delta of 92.4 B per
 * POPULATED entry, against the 88 B this constant predicts for a one-key
 * remainder. Entities with no extra base field hold no entry and measured
 * 46.0 B/entity on a repeat run, i.e. the remainder costs them nothing.
 */
const BASE_METADATA_ENTRY_BYTES = 80;

/**
 * Measured cost of one store's populated remainder entries. Takes the map
 * itself rather than `BaseColumns` so the LINE store — which keeps its own
 * frozen Iter-2 columns but the SAME remainder — accounts for it identically.
 */
export function baseMetadataBytes(
  metadataByIndex: ReadonlyMap<number, BaseMetadata>,
): number {
  let bytes = 0;
  for (const metadata of metadataByIndex.values()) {
    bytes += BASE_METADATA_ENTRY_BYTES + Object.keys(metadata).length * 8;
  }
  return bytes;
}

export const STYLE_FIELDS_PER_ENTITY = 5;
const ARRAY_MODE_VALUES = ["", "rect", "polar", "path"] as const;
export type ArrayModeTag = 0 | 1 | 2 | 3;

export function arrayModeToTag(mode: DrawingEntityBase["arrayMode"]): ArrayModeTag {
  if (mode === "rect") return 1;
  if (mode === "polar") return 2;
  if (mode === "path") return 3;
  return 0;
}

export function tagToArrayMode(tag: number): DrawingEntityBase["arrayMode"] | undefined {
  if (tag <= 0 || tag >= ARRAY_MODE_VALUES.length) return undefined;
  return ARRAY_MODE_VALUES[tag] as DrawingEntityBase["arrayMode"];
}

export interface BaseColumns {
  styles: Uint16Array;
  arrayModes: Uint8Array;
  ids: string[];
  spaceIds: string[];
  arrayGroupIds: Map<number, string>;
  arraySourceOfGroups: Map<number, string>;
  /**
   * Sparse common-metadata remainder — every `DrawingEntityBase` field that
   * has no column above, held verbatim. See `BaseMetadata`. An entity whose
   * file carried none of them holds NO entry, so the entities this store
   * exists to make cheap pay nothing for it.
   */
  metadata: Map<number, BaseMetadata>;
  capacity: number;
  /** post-V9 Section 0 Iter 6.C — 1 byte per slot. 0 = live, 1 = tombstoned. */
  tombstones: Uint8Array;
  /** post-V9 Section 0 Iter 6.C — running count of tombstoned slots. */
  tombstoneCount: number;
}

export const DEFAULT_CAPACITY = 1024;

export function createBaseColumns(initialCapacity: number): BaseColumns {
  const cap = Math.max(1, initialCapacity);
  const ids = new Array<string>(cap).fill("");
  const spaceIds = new Array<string>(cap).fill("");
  return {
    styles: new Uint16Array(cap * STYLE_FIELDS_PER_ENTITY),
    arrayModes: new Uint8Array(cap),
    ids,
    spaceIds,
    arrayGroupIds: new Map(),
    arraySourceOfGroups: new Map(),
    metadata: new Map(),
    capacity: cap,
    tombstones: new Uint8Array(cap),
    tombstoneCount: 0,
  };
}

export function growBaseColumns(columns: BaseColumns, needed: number): number {
  if (needed <= columns.capacity) return columns.capacity;
  let newCap = columns.capacity;
  while (newCap < needed) newCap *= 2;
  const newStyles = new Uint16Array(newCap * STYLE_FIELDS_PER_ENTITY);
  newStyles.set(columns.styles);
  const newArrayModes = new Uint8Array(newCap);
  newArrayModes.set(columns.arrayModes);
  const newTombstones = new Uint8Array(newCap);
  newTombstones.set(columns.tombstones);
  const oldLen = columns.ids.length;
  columns.ids.length = newCap;
  columns.spaceIds.length = newCap;
  for (let i = oldLen; i < newCap; i++) {
    columns.ids[i] = "";
    columns.spaceIds[i] = "";
  }
  columns.styles = newStyles;
  columns.arrayModes = newArrayModes;
  columns.tombstones = newTombstones;
  columns.capacity = newCap;
  return newCap;
}

export function writeBase(columns: BaseColumns, table: SharedStringTable, index: number, entity: DrawingEntityBase): void {
  const s = index * STYLE_FIELDS_PER_ENTITY;
  columns.styles[s + 0] = internString(table, entity.layer);
  // EPIC-COLOR-UNION S1 — intern the LOSSLESS serialized colour. `serializeColor`
  // emits the legacy string verbatim for the common cases (ByLayer / ByBlock /
  // index / plain rgb — byte-identical interned table) and a `!EC:`-prefixed
  // payload for the true-/book-colours whose 62/430 siblings the legacy string
  // would drop. `coerceColor` first defends raw entity literals (tests /
  // reducers) that bypassed the schema and carry a legacy-string or absent
  // `color`. Rehydrated losslessly in `readBase` via `deserializeColor`.
  columns.styles[s + 1] = internString(
    table,
    serializeColor(coerceColor(entity.color)),
  );
  columns.styles[s + 2] = internString(table, entity.linetype);
  columns.styles[s + 3] = internString(table, entity.lineweight);
  columns.styles[s + 4] = internString(table, entity.transparency);
  columns.arrayModes[index] = arrayModeToTag(entity.arrayMode);
  columns.ids[index] = entity.id;
  columns.spaceIds[index] = entity.spaceId;
  if (entity.arrayGroupId !== undefined) columns.arrayGroupIds.set(index, entity.arrayGroupId);
  else columns.arrayGroupIds.delete(index);
  if (entity.arraySourceOfGroup !== undefined) columns.arraySourceOfGroups.set(index, entity.arraySourceOfGroup);
  else columns.arraySourceOfGroups.delete(index);
  // Everything else the base model carries — captured as a REMAINDER, not as
  // an enumerated list, so a field added to `drawingEntityBaseSchema` later
  // cannot be silently dropped on the streamed rebuild. See `BaseMetadata`.
  storeBaseMetadata(columns.metadata, index, entity);
  // post-V9 Section 0 Iter 6.C — fresh writes are always live. This is a
  // no-op during normal append (slot was 0); if Commit 2 ever overwrites
  // a tombstoned slot in-place, that path manages tombstoneCount itself.
  columns.tombstones[index] = 0;
}

/** post-V9 Section 0 Iter 6.C — true if slot at `index` is tombstoned. */
export function isTombstoned(columns: BaseColumns, index: number): boolean {
  if (index < 0 || index >= columns.capacity) return false;
  return columns.tombstones[index] === 1;
}

/**
 * post-V9 Section 0 Iter 6.C — mark slot `index` as tombstoned. Idempotent.
 * Returns true if the call transitioned 0→1 (so caller can decide to log /
 * trigger compaction). Returns false if it was already tombstoned or out of range.
 */
export function markTombstone(columns: BaseColumns, index: number): boolean {
  if (index < 0 || index >= columns.capacity) return false;
  if (columns.tombstones[index] === 1) return false;
  columns.tombstones[index] = 1;
  columns.tombstoneCount += 1;
  return true;
}

/** post-V9 Section 0 Iter 6.C — live slot count = bookkeeping count − tombstones. */
export function liveSlotCount(columns: BaseColumns, count: number): number {
  return Math.max(0, count - columns.tombstoneCount);
}

export function readBase(columns: BaseColumns, table: SharedStringTable, index: number): DrawingEntityBase {
  const s = index * STYLE_FIELDS_PER_ENTITY;
  const result: DrawingEntityBase = {
    id: columns.ids[index] ?? "",
    spaceId: columns.spaceIds[index] ?? "",
    layer: lookupString(table, columns.styles[s + 0] ?? 0),
    // EPIC-COLOR-UNION S1 — rehydrate the interned colour into the typed union.
    // `deserializeColor` restores the full union LOSSLESSLY: the `!EC:`-prefixed
    // rich payload (true-/book-colours with 62/430 siblings) is parsed back
    // verbatim; a plain legacy string routes through `coerceColor` (idempotent).
    // Faithful round-trip of `writeBase`'s `serializeColor`.
    color: deserializeColor(lookupString(table, columns.styles[s + 1] ?? 0)),
    linetype: lookupString(table, columns.styles[s + 2] ?? 0),
    lineweight: lookupString(table, columns.styles[s + 3] ?? 0),
    transparency: lookupString(table, columns.styles[s + 4] ?? 0),
  };
  const mode = tagToArrayMode(columns.arrayModes[index] ?? 0);
  if (mode !== undefined) result.arrayMode = mode;
  const grp = columns.arrayGroupIds.get(index);
  if (grp !== undefined) result.arrayGroupId = grp;
  const src = columns.arraySourceOfGroups.get(index);
  if (src !== undefined) result.arraySourceOfGroup = src;
  applyBaseMetadata(result, columns.metadata.get(index));
  return result;
}

export const BASE_BYTES_PER_ENTITY = STYLE_FIELDS_PER_ENTITY * 2 + 1 + 2 * 8;

export function isDrawingEntity(e: unknown): e is DrawingEntity {
  return typeof e === "object" && e !== null && typeof (e as { type?: unknown }).type === "string";
}
